<template>
  <div class="permission-list-container">
    <el-row :gutter="20" class="operate-bar">
      <el-col :span="8" align="left">
        <!-- 新增权限按钮 -->
        <el-button icon="el-icon-plus" type="primary" @click="openAddDialog(null)"
          v-hasPermission="'permission:edit'">新增根权限</el-button>
      </el-col>
      <!-- 搜索区域 -->
      <el-col :span="16" align="right">
        <el-form :inline="true" class="search-form">
          <el-form-item>
            <el-button icon="el-icon-refresh" style="visibility: hidden;">重置</el-button>
          </el-form-item>
        </el-form>
      </el-col>
    </el-row>

    <!-- 权限树形结构 -->
    <el-tree :data="permissionTree" node-key="id" :props="treeProps" :expand-on-click-node="false"
      :default-expand-all="true" ref="permissionTree" class="permission-tree">
      <span class="custom-tree-node" slot-scope="{ node, data }">
        <span>{{ data.permissionName }}</span>
        <span>
          <el-tag size="mini" v-if="data.url" class="mr-2">{{ data.url }}</el-tag>
          <el-tag size="mini" v-if="data.permissionCode" class="mr-2" type="warning">{{ data.permissionCode }}</el-tag>
          <el-tag size="mini" :type="data.type === 1 ? 'success' : 'info'" class="mr-2">
            {{ data.type === 1 ? '菜单' : '按钮' }}
          </el-tag>
          <el-button type="text" size="mini" v-hasPermission="'permission:edit'" @click="() => openAddDialog(data.id)">
            <i class="el-icon-plus"></i>
          </el-button>
          <el-button type="text" size="mini" v-hasPermission="'permission:edit'" @click="() => openEditDialog(data)">
            <i class="el-icon-edit"></i>
          </el-button>
          <el-button type="text" size="mini" v-hasPermission="'permission:edit'" @click="() => handleDelete(data)"
            :disabled="hasChildren(data)">
            <i class="el-icon-delete"></i>
          </el-button>
        </span>
      </span>
    </el-tree>


    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="上级权限">
          <el-select v-model="form.parentId" placeholder="请选择上级权限" :disabled="true">
            <el-option :value="0" label="无（根权限）"></el-option>
            <el-option v-for="permission in flattenPermissions(permissionTree)" :key="permission.id"
              :label="getPermissionLabel(permission)" :value="Number(permission.id)"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="权限名称" prop="permissionName">
          <el-input v-model="form.permissionName" maxlength="20" placeholder="请输入权限名称"></el-input>
        </el-form-item>
        <el-form-item label="权限编码" prop="permissionCode">
          <el-input v-model="form.permissionCode" placeholder="如user:list, user:add" maxlength="50"></el-input>
        </el-form-item>
        <el-form-item label="请求路径" prop="url">
          <el-input v-model="form.url" placeholder="如/api/user" maxlength="100"></el-input>
        </el-form-item>
        <el-form-item label="请求方法" prop="method">
          <el-select v-model="form.method" placeholder="请选择请求方法">
            <el-option value="">无</el-option>
            <el-option value="GET">GET</el-option>
            <el-option value="POST">POST</el-option>
            <el-option value="PUT">PUT</el-option>
            <el-option value="DELETE">DELETE</el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="权限类型" prop="type">
          <el-radio-group v-model="form.type">
            <el-radio :label="1">菜单</el-radio>
            <el-radio :label="2">按钮</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="form.sort" controls-position="right" :min="0" :max="99"></el-input-number>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false; $refs.form.resetFields()">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import permissionApi from '../../api/permission'
import { hasPermission } from '../../utils/hasPermission'

export default {
  name: 'PermissionList',
  data() {
    // 权限编码唯一校验
    const checkPermissionCodeUnique = async (rule, value, callback) => {
      if (!value) {
        // 空值不校验（由required规则处理）
        return callback()
      }

      try {
        const params = {
          permissionCode: value,
          excludeId: this.isEdit ? this.recordId : null
        }
        // 调用后端查重接口
        const response = await permissionApi.checkPermissionCode(params)
        if (response.success && response.exists) {
          // 权限编码已存在，返回错误提示
          callback(new Error('该权限编码已被使用，请更换'))
        } else {
          // 权限编码可用，验证通过
          callback()
        }
      } catch (error) {
        callback(new Error('检查失败，请稍后重试'))
      }
    }

    return {
      // 权限树形数据
      permissionTree: [],

      // 对话框
      dialogVisible: false,
      dialogTitle: '新增权限',
      isEdit: false,
      recordId: null, // 编辑时记录当前权限的ID，用于排除自身
      form: {
        id: null,
        parentId: 0,
        permissionName: '',
        permissionCode: '',
        url: '',
        method: '',
        type: 1,
        sort: 0
      },
      rules: {
        permissionName: [
          { required: true, message: '请输入权限名称', trigger: 'blur' },
          { min: 1, max: 20, message: '长度在 1 到 20 个字符', trigger: 'blur' }
        ],
        permissionCode: [
          { required: true, message: '请输入权限编码', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9:_\-]+$/, message: '只能包含字母、数字、冒号、下划线和减号', trigger: 'blur' },
          { min: 1, max: 50, message: '长度在 1 到 50 个字符', trigger: 'blur' },
          { validator: checkPermissionCodeUnique, trigger: 'blur' }
        ],
        sort: [
          { required: true, message: '请输入排序号', trigger: 'blur' },
          { type: 'number', min: 0, max: 99, message: '请输入在 0 到 99 之间的数字', trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择权限类型', trigger: 'change' }
        ]
      },

      // 树形结构配置
      treeProps: {
        children: 'children',
        label: 'permissionName'
      }
    }
  },
  created() {
    this.loadPermissions()
  },
  methods: {
    // 加载权限列表
    loadPermissions() {
      permissionApi.findAll().then(response => {
        if (response.success) {
          // 构建权限树形结构
          this.permissionTree = this.buildPermissionTree(response.data.data)
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载权限失败：' + error.message)
      })
    },
    // 递归扁平化树状结构，返回包含所有节点的一维数组
    flattenPermissions(tree) {
      let allPermissions = [];
      tree.forEach(node => {
        // 先添加当前节点
        allPermissions.push(node);
        // 若有子节点，递归处理并合并
        if (node.children && node.children.length > 0) {
          allPermissions = allPermissions.concat(this.flattenPermissions(node.children));
        }
      });
      return allPermissions;
    },

    // 构建权限树形结构
    buildPermissionTree(permissions) {
      const tree = []
      const map = {}

      // 先将所有权限放入map
      permissions.forEach(permission => {
        map[permission.id] = { ...permission, children: permission.children || [] }
      })

      // 构建树形结构
      permissions.forEach(permission => {
        if (permission.parentId === null || permission.parentId === 0) {
          // 根节点
          tree.push(map[permission.id])
        } else {
          // 子节点
          if (map[permission.parentId]) {
            map[permission.parentId].children.push(map[permission.id])
          }
        }
      })

      return tree
    },

    // 打开新增对话框
    openAddDialog(parentId) {
      if (!hasPermission('permission:edit')) {
        this.$message.error('无新增权限权限');
        return;
      }
      this.dialogTitle = parentId ? '新增子权限' : '新增根权限'
      this.isEdit = false
      this.recordId = null
      this.form = {
        id: null,
        parentId: parentId || 0,
        permissionName: '',
        permissionCode: '',
        url: '',
        method: '',
        type: 1,
        sort: 0
      }
      this.dialogVisible = true
    },

    // 打开编辑对话框
    openEditDialog(data) {
      if (!hasPermission('permission:edit')) {
        this.$message.error('无编辑权限权限');
        return;
      }
      this.dialogTitle = '编辑权限'
      this.isEdit = true
      this.recordId = data.id
      this.form = { ...data }
      this.dialogVisible = true
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          if (this.isEdit) {
            // 更新权限
            permissionApi.update(this.form.id, this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '更新成功')
                this.dialogVisible = false
                this.loadPermissions()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('更新失败：' + error.message)
            })
          } else {
            // 新增权限
            permissionApi.add(this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '新增成功')
                this.dialogVisible = false
                this.loadPermissions()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('新增失败：' + error.message)
            })
          }
        }
      })
    },

    // 删除权限
    handleDelete(data) {
      if (!hasPermission('permission:edit')) {
        this.$message.error('无删除权限权限');
        return;
      }
      if (this.hasChildren(data)) {
        this.$message.error('该权限下有子权限，不能删除');
        return;
      }
      this.$confirm('确定要删除该权限吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        permissionApi.delete(data.id).then(response => {
          if (response.success) {
            this.$message.success(response.message || '删除成功')
            this.loadPermissions()
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    // 判断权限是否有子权限
    hasChildren(data) {
      return data.children && data.children.length > 0
    },

    // 获取权限标签（用于下拉选择显示完整路径）
    getPermissionLabel(permission, prefix = '') {
      const label = prefix ? `${prefix} > ${permission.permissionName}` : permission.permissionName

      // 只返回第一层子节点，避免下拉列表过长
      if (!prefix) {
        return label
      }

      return label
    },

    hasPermission(permCode) {
      return hasPermission(permCode);
    },

  }
}
</script>

<style scoped>
.permission-tree {
  min-height: 400px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 10px;
}

.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
}

.mr-2 {
  margin-right: 4px;
}
</style>
